// SPEC-COLLECTION-OFFLINE-COMPLETE-01 (D-268i) PHASE_D: 集金の未送信可視化(D1/D2)+ 警報(D3/FIX_D6)+ 永続化/逼迫(D4/D5)。
//   PRIOR_ART = 巡回の src/hooks/useUnsentBanner.js。同じ custom event('clawops-lf1-changed')購読で再集計する作法を踏襲し、
//   新しい仕組みを発明しない。決定7 に従い「自分の未送信」と「この端末の他人の未送信」を分けて出す。
//   PHASE_D_LIMIT: 未送信は端末内 IDB を読むことでしか検知できないため、警報はアプリを開いた(起動/前面復帰)ときにしか鳴らない。
//     電源オフ/未起動のまま日が過ぎても Sentry には何も上がらない(サーバに「未送信」という概念が原理的に無い)。
import { useCallback, useEffect, useRef, useState } from 'react'
import { getAllUnsyncedDrafts, splitUnsentByStaff, pickStaleAlarm } from '../lib/localStore/collectionDrafts'
import { getAppState, putAppState } from '../lib/localStore/appState'
import { requestPersistentStorage, getStorageEstimate } from '../lib/storage/persist'
import { Sentry } from '../lib/sentry'
import { useAuth } from './useAuth'

const EVENT_NAME = 'clawops-lf1-changed' // 巡回と共有(notifyLfChange が発火)。集金の putDraft/flush 後も飛ぶ。
const ALARM_KEY = 'collection_unsent_alarm' // app_state: { [localId]: 最終警報 ms }(FIX_D6 dedup)

export function useCollectionUnsent() {
  const { staffId } = useAuth()
  const [summary, setSummary] = useState({ mine: 0, others: 0, total: 0, storeCount: 0, oldestMs: null })
  const [storage, setStorage] = useState(null) // { usage, quota, ratio, tight } または null
  const staffRef = useRef(staffId)
  staffRef.current = staffId

  const recompute = useCallback(async () => {
    const drafts = await getAllUnsyncedDrafts()
    setSummary(splitUnsentByStaff(drafts, staffRef.current))
  }, [])

  // D3/FIX_D6 警報 + D4 永続化要求 + D5 逼迫監視。アプリ起動時と前面復帰時に走らせる(PHASE_D_LIMIT)。
  const runAlarmAndStorage = useCallback(async () => {
    requestPersistentStorage().catch(() => {})                       // D4: 毎回要求
    getStorageEstimate().then(est => { if (est) setStorage(est) }).catch(() => {}) // D5: 逼迫監視
    try {
      const drafts = await getAllUnsyncedDrafts()
      const lastMap = (await getAppState(ALARM_KEY)) ?? {}
      const now = Date.now()
      const alarm = pickStaleAlarm(drafts, lastMap, now)
      if (alarm) {
        // D3: 金額・署名・個人名は絶対に送らない。payload は count / oldestAgeMs / store_code のみ。
        Sentry.captureMessage('collection unsent stale', {
          level: 'warning',
          tags: { diag: 'D-268' },
          extra: alarm.payload,
        })
        // dedup 記録を更新。生存 localId のみ残す(送信/掃除で消えた localId を溜めない)。
        const alive = new Set(
          drafts.filter(d => d && d.synced !== true && d.status === 'complete').map(d => d.localId)
        )
        const next = { ...lastMap, [alarm.localId]: now }
        for (const k of Object.keys(next)) if (!alive.has(k)) delete next[k]
        await putAppState(ALARM_KEY, next)
      }
    } catch { /* 警報失敗は業務を止めない */ }
  }, [])

  useEffect(() => {
    recompute()
    runAlarmAndStorage()
    const onChange = () => { recompute() }
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') { recompute(); runAlarmAndStorage() }
    }
    if (typeof window !== 'undefined') window.addEventListener(EVENT_NAME, onChange)
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible)
    return () => {
      if (typeof window !== 'undefined') window.removeEventListener(EVENT_NAME, onChange)
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible)
    }
  }, [recompute, runAlarmAndStorage])

  // staffId 変更で「自分/他人」の按分を引き直す。
  useEffect(() => { recompute() }, [staffId, recompute])

  return { ...summary, storage, refresh: recompute }
}
